import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from '@/lib/api';
import { track } from '@/lib/observability';
import {
  adminUserFromSession,
  parseLoginResponse,
  twoFactorChallengeSchema,
  type AdminUser,
  type LoginInput,
  type LoginResult,
  type TwoFactorChallenge,
} from './schema';
import { clearSessionState } from './session';

export const authKeys = {
  me: ['auth', 'me'] as const,
};

/**
 * FIX(admin-401-clears-cache): drop every cached query except the identity —
 * the previous session's lists/details must never render for whoever signs in
 * next. Shared by logout, the global 401 handler and a sign-in as someone else.
 */
export function removeNonIdentityQueries(qc: QueryClient): void {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== authKeys.me[0] });
}

/**
 * FIX(admin-tracker-cross-org): the caller's active organisation id, read from
 * the cached `/me` (always loaded before a protected page renders) without
 * subscribing or fetching — so a page that only needs it for copy doesn't need
 * the AuthProvider in scope. Null when unknown.
 */
export function useActiveWorkspaceId(): string | null {
  const qc = useQueryClient();
  return qc.getQueryData<AdminUser | null>(authKeys.me)?.activeWorkspaceId ?? null;
}

/** Bootstrap the session. 401/403 -> null (not logged in), not an error toast. */
export function useMe() {
  return useQuery<AdminUser | null>({
    queryKey: authKeys.me,
    queryFn: async ({ signal }) => {
      try {
        // FIX(admin-reauth-401): a 401 here is the ordinary "logged out" answer,
        // handled by returning null (ProtectedRoute then redirects) — it must not
        // also fire the global "session expired" listener.
        const raw = await apiClient.get<unknown>('/api/auth/me', { signal, suppressUnauthorized: true });
        return adminUserFromSession(raw);
      } catch (err) {
        // FIX(admin-me-403-disabled): a DISABLED account gets `403 {error:
        // 'account disabled'}` from requireAuth. Before: only 401 meant signed
        // out, so the 403 reached AuthProvider's "Can't load your session"
        // retry screen — a dead end with no way to sign out. After: 403 is also
        // "no usable session" → null → /login, where signing in again explains
        // the account is disabled.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return null;
        throw err;
      }
    },
    // FIX(admin-401-cache-loop): a transient 5xx/network failure of /me is
    // retried (it is the one query everything hangs off) but a 4xx is final.
    // Before: `retry: false`, so one blip surfaced as "logged out". After: up to
    // two retries for non-4xx; if it still fails, AuthProvider shows a retry
    // screen instead of redirecting a valid session to /login.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
      return failureCount < 2;
    },
    staleTime: 60_000,
  });
}

/** Put a freshly issued session into the cache — the single path both the
 *  one-step login and the 2FA step go through, so they cannot drift. */
function seedSession(qc: QueryClient, user: AdminUser) {
  // A new session never inherits the previous one's step-up elevation.
  clearSessionState();
  // FIX(admin-401-clears-cache): nor another user's cached data. Before: only
  // `me` was replaced, so the previous admin's lists could render for the next
  // one until each refetched. After: anything cached is dropped unless the
  // same account is signing back in (an unknown previous identity counts as a
  // different one).
  const previous = qc.getQueryData<AdminUser | null>(authKeys.me);
  if (previous?.id !== user.id) removeNonIdentityQueries(qc);
  qc.setQueryData(authKeys.me, user);
  track({ action: 'auth.login', result: 'success', target: user.id });
}

/**
 * Step one: email + password. Resolves to either a session (cache seeded) or a
 * 2FA challenge (no session yet — the caller moves to the code step).
 */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation<LoginResult, unknown, LoginInput>({
    mutationFn: async (input) => {
      // FIX(admin-reauth-401): a 401 here is "wrong email or password", shown
      // inline on the form — never a session-expiry redirect.
      const raw = await apiClient.post<unknown>('/api/auth/login', input, { suppressUnauthorized: true });
      return parseLoginResponse(raw);
    },
    onSuccess: (result) => {
      if (result.kind === 'session') seedSession(qc, result.user);
      else track({ action: 'auth.login.2fa.challenged', result: 'success' });
    },
    onError: () => track({ action: 'auth.login', result: 'failure' }),
  });
}

export interface VerifyTwoFactorInput {
  challengeId: string;
  code: string;
  /** Skip the code on this browser for 30 days (server `td` cookie). */
  rememberDevice: boolean;
}

/**
 * FIX(admin-login-2fa): step two — exchange the challenge handle + emailed code
 * for a session (`POST /api/auth/login/2fa`). On success the cache is seeded
 * exactly as a one-step login does.
 */
export function useVerifyTwoFactor() {
  const qc = useQueryClient();
  return useMutation<AdminUser, unknown, VerifyTwoFactorInput>({
    mutationFn: async (input) => {
      const raw = await apiClient.post<unknown>(
        '/api/auth/login/2fa',
        { challengeId: input.challengeId, code: input.code.trim(), rememberDevice: input.rememberDevice },
        { suppressUnauthorized: true },
      );
      return adminUserFromSession(raw);
    },
    onSuccess: (user) => seedSession(qc, user),
    onError: () => track({ action: 'auth.login.2fa', result: 'failure' }),
  });
}

/** FIX(admin-login-2fa): mail a fresh code for the same challenge
 *  (`POST /api/auth/login/2fa/resend`; cooldown + cap enforced server-side). */
export function useResendTwoFactor() {
  return useMutation<TwoFactorChallenge, unknown, { challengeId: string }>({
    mutationFn: async ({ challengeId }) => {
      const raw = await apiClient.post<unknown>(
        '/api/auth/login/2fa/resend',
        { challengeId },
        { suppressUnauthorized: true },
      );
      return twoFactorChallengeSchema.parse(raw);
    },
    // The login page renders resend errors inline.
    meta: { suppressErrorToast: true },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post('/api/auth/logout'),
    // FIX(admin-logout-clears-elevation): clear local session state whether or
    // not the request succeeded — a network failure must not leave the admin
    // "signed in" in this tab with a live step-up window. Before: onSuccess
    // cleared only the query cache. After: onSettled clears the cache, the
    // step-up store and the legacy impersonation store.
    onSettled: (_data, error) => {
      clearSessionState();
      void qc.cancelQueries();
      qc.setQueryData(authKeys.me, null);
      removeNonIdentityQueries(qc);
      track({ action: 'auth.logout', result: error ? 'failure' : 'success' });
    },
    // Local sign-out already happened; the server-side failure is not the
    // admin's problem to act on.
    meta: { suppressErrorToast: true },
  });
}
